// The client's side of the netcode:
//  - clock: estimate the server tick, run our own sim ahead of it, nudge rate off the cushion
//  - prediction: step our cowboy locally the moment input happens; rewind + replay on snapshots
//  - interpolation: draw everyone else ~2 snapshots in the past, adaptive to jitter
//  - events + enemy bullets (spawned once, flown locally)

import { HISTORY_TICKS, INPUT_REDUNDANCY, TICK_RATE, clamp, lerp, lerpAngle } from '../../sim/core';
import { Ev, GameEvent, Reader, Writer } from '../../sim/codec';
import { BKind, BULLETS, Bullet, advanceBullet, bulletHitsCapsule, makeBullet } from '../../sim/bullets';
import { ClassId, ENEMIES, PLAYER_RADIUS, isBossType } from '../../sim/defs';
import { InputFrame, PLAYER_FIELDS, PlayerState, Solid, StepOut, clonePlayer, newPlayer, newStepOut, stepPlayer } from '../../sim/player';
import { NetEnemy, RemotePlayer, S2C, Snapshot, decEvents, decSnapshot, decWelcome, encHello, encInputs, encPing, encSetClass, encSetLoadout, encReady, encSit, encMark, encSetContract, encPickCard, encSetLook, quantizeInput } from '../../sim/protocol';
import { World } from '../../sim/world';
import { quantizePose } from '../../sim/destruct';
import { LagSim, Transport, WsTransport } from './transport';

const MS_PER_TICK = 1000 / TICK_RATE;
const PRED_HISTORY = 256;
const NO_SOLIDS: readonly Solid[] = [];

export interface PredictedFrame { tick: number; input: InputFrame; state: PlayerState; /** the robots the step collided with (replayed on reconcile) */ solids: readonly Solid[] }

export interface NetStats {
  ping: number; jitter: number; loss: number; cushion: number; corrections: number; interp: number;
  kbpsIn: number; kbpsOut: number; tickRate: number; snapshotsPerSec: number;
}

export interface SessionHooks {
  /** a locally predicted tick produced actions (shots etc.); never called during replays */
  onPredicted(p: PlayerState, input: InputFrame, out: StepOut, tick: number): void;
  /** an event is due (world events are delayed to the interpolated render time) */
  onEvent(e: GameEvent, tick: number): void;
  onWelcome(pid: number, seed: number): void;
  /** the posse warped: session.world is already the new island */
  onIsland?(index: number): void;
  onClose(reason: string): void;
}

export class NetSession {
  transport: Transport;
  hooks: SessionHooks;
  pid = -1;
  slot = 0;
  seed = 0;
  world: World | null = null;
  /** load-hitch probe only (tools/hitchprobe.mjs): keep a locally swapped island instead of following the server's */
  pinIsland = false;
  /** an island already made ahead of the warp (client/render/world/prefetch.ts) */
  worldFor: ((seed: number, index: number) => World | null) | null = null;
  connected = false;
  token = '';

  // ---- clock
  // The server's tick is estimated from snapshot arrivals, not assumed to run at exactly 60 Hz:
  // a Durable Object can tick slowly for its first seconds and stutter under load. `srvRate` is a
  // least-squares fit of tick against arrival time; `est` follows the least-delayed arrival line.
  private est = 0; // estimated server tick now (fractional)
  private srvRate = TICK_RATE / 1000; // server ticks per ms
  private arrivals: { tick: number; t: number }[] = [];
  private haveClock = false;
  rtt = 100;
  private rttVar = 0;
  simTick = 0; // last predicted tick
  private tickClock = 0; // fractional, advances with time * server rate * rate
  private rate = 1;
  cushion = 0;
  private cushionAt = 0; // latest snapshot tick that carried a cushion reading
  private jumpGuard = 0; // no second catch-up jump until the first one has shown up in the cushion

  // ---- prediction
  me: PlayerState | null = null;
  private prevMe: PlayerState | null = null;
  private hist: (PredictedFrame | undefined)[] = new Array(PRED_HISTORY);
  smoothX = 0; smoothY = 0; smoothZ = 0;
  private out = newStepOut();
  private sendBuf: InputFrame[] = [];
  private w = new Writer(256);

  // ---- snapshots / interpolation
  snaps: Snapshot[] = [];
  latest: Snapshot | null = null;
  private snapArrive: { tick: number; t: number }[] = [];
  interpDelay = 4; // ticks
  private jitterTicks = 0;
  renderTick = 0;

  // ---- events
  private pending: { tick: number; e: GameEvent }[] = [];
  bullets = new Map<number, Bullet & { shownAt: number; catchup: number; predictedHit: boolean }>();

  // ---- stats
  stats: NetStats = { ping: 0, jitter: 0, loss: 0, cushion: 0, corrections: 0, interp: 0, kbpsIn: 0, kbpsOut: 0, tickRate: TICK_RATE, snapshotsPerSec: 0 };
  private corrWindow: number[] = [];
  private bytesMark = { t: 0, in: 0, out: 0 };
  private snapCount = 0;
  private snapGap = { expected: 0, got: 0 };

  private dial: { url: string; sim: LagSim; name: string; cls: ClassId };

  constructor(url: string, sim: LagSim, hooks: SessionHooks, name: string, cls: ClassId, token: string) {
    this.hooks = hooks;
    this.token = token;
    this.dial = { url, sim, name, cls };
    this.transport = this.open();
  }

  private open(): Transport {
    const t = new WsTransport(this.dial.url, this.dial.sim);
    t.onOpen = () => {
      if (t !== this.transport) return;
      this.connected = true;
      t.send(encHello(this.w, this.dial.name, this.dial.cls, this.token), true);
    };
    t.onClose = (r) => { if (t !== this.transport) return; this.connected = false; this.hooks.onClose(r); };
    t.onMessage = (d) => { if (t === this.transport) this.onMessage(d); };
    return t;
  }

  /** The socket went silent without closing (a frozen path won't finish a close handshake either):
   *  give up on it now, and the reconnect replaces it. */
  drop(reason: string) {
    if (!this.connected) return;
    this.connected = false;
    this.hooks.onClose(reason);
  }

  /** Dial again with the slot token. A server that kept the room hands the slot back; a fresh one
   *  (a deploy restarts every room) sends a new seed, and the welcome rebuilds the world. */
  reconnect() {
    if (this.connected) return;
    const old = this.transport;
    this.transport = this.open();
    try { old.close(); } catch { /* already closed */ }
  }

  /** A ping or quick-chat line (MarkKind); the server rate-limits and rebroadcasts it as Ev.Mark. */
  mark(kind: number, x: number, y: number, z: number, eid: number) {
    this.transport.send(encMark(this.w, kind, x, y, z, eid), true);
  }
  /** saloon: saddle up / not yet (sim/saloon.ts) */
  setReady(on: boolean) { this.transport.send(encReady(this.w, on), true); }
  /** saloon: sit on SEATS[seat], or stand (NO_SEAT) */
  sit(seat: number) { if (this.connected) this.transport.send(encSit(this.w, seat), true); }
  setClass(cls: ClassId) {
    this.dial.cls = cls;
    this.transport.send(encSetClass(this.w, cls), true);
  }

  setLoadout(primary: number, secondary: number, grenade: number) {
    if (this.connected) this.transport.send(encSetLoadout(this.w, primary, secondary, grenade), true);
  }

  setLook(finish: number, contracts: number, title: string) {
    if (this.connected) this.transport.send(encSetLook(this.w, finish, contracts, title), true);
  }

  pickCard(slot: number, wslot = 255) { if (this.connected) this.transport.send(encPickCard(this.w, slot, wslot), true); }
  setContract(contract: number) {
    if (this.connected) this.transport.send(encSetContract(this.w, contract), true);
  }

  /** when the last message of any kind arrived: a silent socket is a dead one */
  lastMsgAt = 0;

  private onMessage(data: ArrayBuffer) {
    this.lastMsgAt = performance.now();
    const r = new Reader(data);
    const type = r.u8();
    const now = performance.now();
    switch (type) {
      case S2C.Welcome: {
        const m = decWelcome(r);
        this.pid = m.pid; this.seed = m.seed; this.token = m.token; this.slot = m.slot;
        // a rejoin: the server's tick may have restarted, so nothing from before it counts
        this.latest = null; this.snaps.length = 0; this.snapArrive.length = 0; this.pending.length = 0;
        this.bullets.clear(); this.hist.fill(undefined); this.sendBuf.length = 0; this.prevMe = null;
        if (!this.world || this.world.runSeed !== m.seed || this.world.index !== m.island) { this.world = this.worldFor?.(m.seed, m.island) ?? new World(m.seed, m.island); this.flushDestruct(); }
        // provisional clock until snapshots arrive
        this.arrivals.length = 0;
        this.arrivals.push({ tick: m.tick, t: now });
        this.est = m.tick;
        this.srvRate = TICK_RATE / 1000;
        this.haveClock = true;
        this.tickClock = m.tick + 6;
        this.simTick = Math.floor(this.tickClock);
        this.me = null;
        this.hooks.onWelcome(m.pid, m.seed);
        for (let i = 0; i < 5; i++) setTimeout(() => this.ping(), i * 60);
        break;
      }
      case S2C.Pong: {
        const t = r.f64(), st = r.f64();
        const rtt = now - t;
        this.rttVar = this.rttVar * 0.8 + Math.abs(rtt - this.rtt) * 0.2;
        this.rtt = this.rtt * 0.8 + rtt * 0.2;
        void st;
        break;
      }
      case S2C.Snapshot: {
        const s = decSnapshot(r);
        if (this.latest && s.tick <= this.latest.tick) break; // out of order datagram
        this.snapCount++;
        if (this.latest) { this.snapGap.expected += (s.tick - this.latest.tick) / 2; this.snapGap.got += 1; }
        this.latest = s;
        this.snaps.push(s);
        while (this.snaps.length > 40) this.snaps.shift();
        this.trackJitter(s.tick, now);
        this.cushion = s.cushion;
        // warp: every machine rebuilds the same island from the seed before replaying onto it
        if (s.run && this.world && s.run.index !== this.world.index && !this.pinIsland) {
          this.world = this.worldFor?.(this.seed, s.run.index) ?? new World(this.seed, s.run.index);
          this.flushDestruct();
          this.bullets.clear();
          this.snaps = [s];
          this.hooks.onIsland?.(s.run.index);
        }
        this.cushionAt = s.tick;
        if (s.me) this.reconcile(s);
        break;
      }
      case S2C.Events: {
        const { tick, evs } = decEvents(r);
        for (const e of evs) this.routeEvent(e, tick);
        break;
      }
    }
  }

  private lastPingAt = 0;
  private ping() {
    if (!this.connected) return;
    this.lastPingAt = performance.now();
    this.transport.send(encPing(this.w, this.lastPingAt), true);
  }

  /** A ping at least once a second from a plain timer, so it still goes out when the frame loop has
   *  stopped (a hidden tab, a minimised window). On Cloudflare every message we send resets the
   *  room's 30 s CPU allowance; a room that hears nothing for that long is killed and the run with it. */
  heartbeat() {
    if (performance.now() - this.lastPingAt >= 1000) this.ping();
  }

  // ------------------------------------------------------------------ clock
  serverTickNow(now = performance.now()) {
    void now;
    return this.est;
  }

  private trackJitter(tick: number, now: number) {
    const A = this.arrivals;
    A.push({ tick, t: now });
    while (A.length > 2 && now - A[0].t > 2500) A.shift();
    // server rate: least squares over the window (needs half a second of history)
    if (A.length >= 8 && now - A[0].t > 500) {
      let mt = 0, mk = 0;
      for (const a of A) { mt += a.t; mk += a.tick; }
      mt /= A.length; mk /= A.length;
      let num = 0, den = 0;
      for (const a of A) { num += (a.t - mt) * (a.tick - mk); den += (a.t - mt) * (a.t - mt); }
      if (den > 0) this.srvRate = clamp(num / den, 10 / 1000, 66 / 1000);
    }
    // jitter = spread of arrival delay around the least-delayed line
    let hi = -Infinity, sum = 0;
    for (const a of A) { const o = a.tick - this.srvRate * a.t; if (o > hi) hi = o; sum += o; }
    this.jitterTicks = this.jitterTicks * 0.8 + (hi - sum / A.length) * 2 * 0.2;
  }

  /** the least-delayed arrival line, evaluated now */
  private estTarget(now: number) {
    let hi = -Infinity;
    for (const a of this.arrivals) { const o = a.tick - this.srvRate * a.t; if (o > hi) hi = o; }
    return this.srvRate * now + hi;
  }

  /**
   * Advance the predicted sim to "now". Returns the fractional alpha between the last two
   * predicted ticks for render interpolation.
   */
  update(dt: number, sample: (tick: number) => InputFrame): number {
    if (!this.world || this.pid < 0 || !this.haveClock) return 0;
    const now = performance.now();
    // ---- server clock: glide toward the arrival line, never jump unless wildly off
    this.est += dt * 1000 * this.srvRate;
    const tgt = this.estTarget(now);
    if (Math.abs(tgt - this.est) > 30) this.est = tgt;
    else this.est += (tgt - this.est) * Math.min(1, dt * 3);
    // ---- our sim clock: run ahead so each input lands ~2 ticks before the server needs it.
    // Steered only by what the server reports (how early inputs arrive), proportionally; a big
    // shortfall jumps forward (the skipped ticks are skipped on both ends), never backward
    // (that would freeze your cowboy). Too far ahead just slows the clock down.
    const want = 2 + Math.min(3, this.jitterTicks * 0.5);
    if (this.latest) {
      const err = want - this.cushion;
      if (err > 12 && this.cushionAt > 0 && now > this.jumpGuard) {
        // starved: catch up now. The skipped ticks are not simulated (no inputs sent for them; the server
        // skips the gap the same way), so the cowboy doesn't fast-forward across the screen. The server's
        // cushion is the minimum over its last 20 input packets (~333 ms), so hold off until that window
        // has seen only inputs sent after this jump, or the same stale sample fires it again.
        this.tickClock += err;
        if (this.me) this.simTick = Math.max(this.simTick, Math.floor(this.tickClock) - 1);
        this.jumpGuard = now + this.rtt + 450;
      }
      const k = err > 0 ? 0.025 : 0.015;
      const lim = this.cushion > want + 15 ? 0.25 : 0.07;
      const r = 1 + clamp(err * k, -lim, lim);
      this.rate += (r - this.rate) * Math.min(1, dt * 8);
    } else {
      // no snapshot yet: hold the provisional lead off the arrival line
      const lead = this.est + (this.rtt / 2) / MS_PER_TICK + 3;
      if (lead > this.tickClock) this.tickClock = lead;
    }
    this.tickClock += dt * 1000 * this.srvRate * this.rate;

    // interpolation clock for remote entities
    const delayTarget = clamp(2 + 1.5 + this.jitterTicks * 1.5, 3, 14);
    this.interpDelay += (delayTarget - this.interpDelay) * Math.min(1, dt * 2);
    this.renderTick = this.est - this.interpDelay;
    this.flushEvents();

    if (!this.me) return 0;
    // after a long frame, skip straight to the newest 8 ticks so the inputs we send are still
    // ahead of the server (sending stale ones would just be discarded)
    if (Math.floor(this.tickClock) - this.simTick > 8) this.simTick = Math.floor(this.tickClock) - 8;
    let steps = 0;
    while (this.simTick < Math.floor(this.tickClock) && steps < 8) {
      this.simTick++;
      steps++;
      const inp = quantizeInput(sample(this.simTick));
      inp.viewTick = this.renderTick;
      this.prevMe = clonePlayer(this.me);
      // the robots as seen at the view tick are solid (sim/player.ts pushOutOfSolids); the server uses the same view
      const solids = this.solidsNow();
      const out = stepPlayer(this.me, inp, this.world, this.out, solids);
      this.hist[this.simTick % PRED_HISTORY] = { tick: this.simTick, input: inp, state: clonePlayer(this.me), solids };
      this.hooks.onPredicted(this.me, inp, out, this.simTick);
      // send: newest first with redundancy
      this.sendBuf.unshift(inp);
      if (this.sendBuf.length > INPUT_REDUNDANCY) this.sendBuf.length = INPUT_REDUNDANCY;
      this.transport.send(encInputs(this.w, this.sendBuf), false);
    }
    // decay the visual correction offset: small ones vanish in ~60 ms, big ones glide over ~160 ms
    const err = Math.hypot(this.smoothX, this.smoothY, this.smoothZ);
    const k = Math.exp(-dt / (0.06 + 0.1 * Math.min(1, err / 1.5)));
    this.smoothX *= k; this.smoothY *= k; this.smoothZ *= k;
    this.stepBullets();
    this.updateStats(now);
    return clamp(this.tickClock - Math.floor(this.tickClock), 0, 1);
  }

  /** previous predicted state, for render interpolation */
  get prev() { return this.prevMe; }

  // ------------------------------------------------------------------ reconciliation
  private reconcile(s: Snapshot) {
    if (!this.world) return; // under packet loss a snapshot can beat the welcome
    const meSrv = s.me!;
    if (!this.me) {
      this.me = newPlayer(this.pid, this.slot, '', meSrv.cls as ClassId, this.world!);
      for (const f of PLAYER_FIELDS) this.me[f] = meSrv[f];
      this.prevMe = clonePlayer(this.me);
      this.simTick = Math.max(this.simTick, s.ack);
      return;
    }
    const before = { x: this.me.x, y: this.me.y, z: this.me.z };
    const h = this.hist[s.ack % PRED_HISTORY];
    let posErr = 1e9;
    if (h && h.tick === s.ack) posErr = Math.hypot(h.state.x - meSrv.x, h.state.y - meSrv.y, h.state.z - meSrv.z);
    // rewind to the server's truth at ack, replay everything we've predicted since
    const st = clonePlayer(this.me);
    for (const f of PLAYER_FIELDS) st[f] = meSrv[f];
    for (let t = s.ack + 1; t <= this.simTick; t++) {
      const fr = this.hist[t % PRED_HISTORY];
      if (!fr || fr.tick !== t) continue;
      stepPlayer(st, fr.input, this.world!, this.out, fr.solids);
      fr.state = clonePlayer(st);
    }
    this.me = st;
    const dx = before.x - st.x, dy = before.y - st.y, dz = before.z - st.z;
    const d = Math.hypot(dx, dy, dz);
    if (d > 4) { this.smoothX = this.smoothY = this.smoothZ = 0; this.prevMe = clonePlayer(st); } // teleport: snap
    else { this.smoothX += dx; this.smoothY += dy; this.smoothZ += dz; if (this.prevMe) { this.prevMe.x -= dx; this.prevMe.y -= dy; this.prevMe.z -= dz; } }
    if (posErr > 0.03 && posErr < 1e8) this.corrWindow.push(performance.now());
  }

  // ------------------------------------------------------------------ interpolation
  /** Bracketing snapshots for the render tick. */
  private bracket(): [Snapshot, Snapshot, number] | null {
    const n = this.snaps.length;
    if (!n) return null;
    const t = this.renderTick;
    for (let i = n - 1; i > 0; i--) {
      const a = this.snaps[i - 1], b = this.snaps[i];
      if (a.tick <= t && t <= b.tick) return [a, b, (t - a.tick) / Math.max(1, b.tick - a.tick)];
    }
    const last = this.snaps[n - 1];
    if (t > last.tick) return [last, last, 0]; // starved: hold
    return [this.snaps[0], this.snaps[0], 0];
  }

  private solidEnemies: NetEnemy[] = [];
  /** the interpolated robots near me, as solids for this predicted tick (a fresh small array: it's kept in the history) */
  private solidsNow(): readonly Solid[] {
    const me = this.me!;
    if (me.paused || me.skyT > 0) return NO_SOLIDS;
    const n = this.enemies(this.solidEnemies);
    let out: Solid[] | null = null;
    for (let i = 0; i < n; i++) {
      const e = this.solidEnemies[i];
      if (isBossType(e.type) || e.st === 5 || e.hp <= 0) continue;
      if (Math.abs(e.x - me.x) > 5 || Math.abs(e.z - me.z) > 5 || Math.abs(e.y - me.y) > 6) continue;
      const d = ENEMIES[e.type];
      (out ??= []).push({ x: e.x, z: e.z, y0: e.y, y1: e.y + d.headY + 0.2, r: d.radius });
    }
    return out ?? NO_SOLIDS;
  }

  private eA = new Map<number, NetEnemy>();
  /** Interpolated robots, written into out (pooled). Returns count. */
  enemies(out: NetEnemy[]): number {
    const br = this.bracket();
    if (!br) return 0;
    const [a, b, f] = br;
    this.eA.clear();
    for (const e of a.enemies) this.eA.set(e.id, e);
    let n = 0;
    for (const eb of b.enemies) {
      const ea = this.eA.get(eb.id) ?? eb;
      const o = out[n] ?? (out[n] = { ...eb });
      o.id = eb.id; o.type = eb.type; o.st = f < 0.5 ? ea.st : eb.st;
      o.prog = ea.st === eb.st ? lerp(ea.prog, eb.prog, f) : (f < 0.5 ? ea.prog : eb.prog);
      o.x = lerp(ea.x, eb.x, f); o.y = lerp(ea.y, eb.y, f); o.z = lerp(ea.z, eb.z, f);
      o.yaw = lerpAngle(ea.yaw, eb.yaw, f);
      o.hp = eb.hp; o.shield = eb.shield; o.flags = eb.flags; o.flags2 = eb.flags2; o.ext = eb.ext; o.parts = eb.parts; o.primedBy = eb.primedBy; o.target = eb.target;
      o.aimX = eb.aimX; o.aimY = eb.aimY; o.aimZ = eb.aimZ;
      n++;
    }
    return n;
  }

  private pA = new Map<number, RemotePlayer>();
  players(out: RemotePlayer[]): number {
    const br = this.bracket();
    if (!br) return 0;
    const [a, b, f] = br;
    this.pA.clear();
    for (const p of a.players) this.pA.set(p.id, p);
    let n = 0;
    for (const pb of b.players) {
      const pa = this.pA.get(pb.id) ?? pb;
      const o = out[n] ?? (out[n] = { ...pb });
      Object.assign(o, pb);
      o.x = lerp(pa.x, pb.x, f); o.y = lerp(pa.y, pb.y, f); o.z = lerp(pa.z, pb.z, f);
      o.vx = lerp(pa.vx, pb.vx, f); o.vy = lerp(pa.vy, pb.vy, f); o.vz = lerp(pa.vz, pb.vz, f);
      o.yaw = lerpAngle(pa.yaw, pb.yaw, f); o.pitch = lerp(pa.pitch, pb.pitch, f);
      o.skyT = lerp(pa.skyT, pb.skyT, f);
      n++;
    }
    return n;
  }

  // ------------------------------------------------------------------ events
  private routeEvent(e: GameEvent, tick: number) {
    switch (e.t) {
      // destruction changes collision: applied to the predicted world on arrival (sim/destruct.ts)
      case Ev.Building: case Ev.Topple: case Ev.StoneRest: this.applyDestruct(e); return;
      // a hole punched through a wall: collision-free, so it's drawn as soon as it lands (your own was predicted)
      case Ev.Breach: this.applyDestruct(e); this.hooks.onEvent(e, tick); return;
      // a new run back on the same island number: the old island's wreckage goes with it
      case Ev.Island:
        if (this.world && this.world.index === e.f[0] && this.world.dest.dirty) {
          this.world = new World(this.seed, e.f[0]);
          this.hooks.onIsland?.(e.f[0]);
        }
        this.hooks.onEvent(e, tick); return;
      case Ev.BulletSpawn: this.spawnBullet(e); this.hooks.onEvent(e, tick); return;
      case Ev.BulletEnd: {
        const b = this.bullets.get(e.f[0]);
        if (b) { this.bullets.delete(b.id); this.hooks.onEvent(e, tick); }
        return;
      }
      // about you, or about the run: show now
      case Ev.PlayerHurt: case Ev.Notice: case Ev.Roster: case Ev.Lives: case Ev.Wave:
      case Ev.Down: case Ev.Revive: case Ev.Respawn: case Ev.Chain:
      case Ev.Run: case Ev.RunEnd: case Ev.Bell:
        this.hooks.onEvent(e, tick); return;
      case Ev.EnemyHit: case Ev.Shot: case Ev.Melee: case Ev.Special:
        if (e.f[e.t === Ev.EnemyHit ? 3 : 0] === this.pid) { this.hooks.onEvent(e, tick); return; } // your own: confirm now
    }
    // world events wait for the interpolated view to reach them, so a robot dies where you see it
    this.pending.push({ tick, e });
  }

  /** destruction events for an island we haven't warped to yet wait for it */
  private destructLater: GameEvent[] = [];
  private applyDestruct(e: GameEvent) {
    const w = this.world;
    if (!w || e.f[0] !== w.index) { if (!w || e.f[0] > w.index) this.destructLater.push(e); return; }
    if (e.t === Ev.Building) w.dest.applyBuilding(e.f[1], e.f[2], e.f[3]);
    else if (e.t === Ev.Topple) w.dest.applyTopple(e.f[1], e.f[2], e.f[3], e.f[4], e.f[5], e.f[6] === 1);
    else if (e.t === Ev.Breach) w.dest.applyBreach(e.f[1], e.f[2], e.f[3], e.f[4], e.f[5]);
    else w.dest.applyRest(e.f[1], e.f[2], quantizePose({ x: e.f[3], y: e.f[4], z: e.f[5], qx: e.f[6], qy: e.f[7], qz: e.f[8], qw: e.f[9] }));
  }
  private flushDestruct() {
    if (!this.destructLater.length) return;
    const q = this.destructLater;
    this.destructLater = [];
    for (const e of q) this.applyDestruct(e);
  }

  private flushEvents() {
    if (!this.pending.length) return;
    let i = 0;
    for (; i < this.pending.length; i++) {
      const p = this.pending[i];
      if (p.tick > this.renderTick + 0.5) break;
      this.hooks.onEvent(p.e, p.tick);
    }
    if (i) this.pending.splice(0, i);
    // never let the queue lag a full second behind
    while (this.pending.length && this.pending[0].tick < this.renderTick - TICK_RATE) this.hooks.onEvent(this.pending.shift()!.e, 0);
  }

  // ------------------------------------------------------------------ bullets
  private spawnBullet(e: GameEvent) {
    const f = e.f;
    const b = makeBullet(f[0], f[1] as BKind, f[2], f[3], f[4], f[5], f[6], f[7], f[8], f[9]) as Bullet & { shownAt: number; catchup: number; predictedHit: boolean };
    // it was fired at server tick f[2]; we're ahead in the present. Pull it from the muzzle
    // up to its true position over ~250 ms so it never pops into existence mid-air.
    b.catchup = Math.max(0, this.simTick - b.tick0);
    b.shownAt = performance.now();
    b.predictedHit = false;
    this.bullets.set(b.id, b);
  }

  private stepBullets() {
    if (!this.world) return;
    const me = this.me;
    for (const b of this.bullets.values()) {
      if (b.predictedHit) continue;
      const res = advanceBullet(b, this.simTick, this.world);
      if (me && BULLETS[b.kind].explode === 0 && me.phase === 0 && !me.paused && me.skyT < 0.35 && me.iframes <= 0
        && bulletHitsCapsule(b, me.x, me.y, me.z, PLAYER_RADIUS)) {
        b.predictedHit = true; // hide it now; the damage arrives with the server's word
        this.hooks.onEvent({ t: Ev.BulletEnd, f: [b.id, b.x, b.y, b.z, this.pid] }, this.simTick);
        continue;
      }
      if (res === 2) this.bullets.delete(b.id);
      else if (res === 1 && BULLETS[b.kind].explode === 0) {
        b.predictedHit = true;
        this.hooks.onEvent({ t: Ev.BulletEnd, f: [b.id, b.x, b.y, b.z, 255] }, this.simTick);
      }
    }
  }

  /** Bullet display position: the true present, minus a decaying catch-up from the muzzle. */
  bulletDisplayTick(b: { tick0: number; catchup: number; shownAt: number }, alpha: number) {
    const age = (performance.now() - b.shownAt) / 250;
    const lag = b.catchup * Math.max(0, 1 - age);
    return this.simTick - 1 + alpha - lag;
  }

  // ------------------------------------------------------------------ stats
  private updateStats(now: number) {
    while (this.corrWindow.length && now - this.corrWindow[0] > 1000) this.corrWindow.shift();
    if (now - this.bytesMark.t > 1000) {
      const dt = (now - this.bytesMark.t) / 1000;
      this.stats.kbpsIn = (this.transport.bytesIn - this.bytesMark.in) / 1024 / dt;
      this.stats.kbpsOut = (this.transport.bytesOut - this.bytesMark.out) / 1024 / dt;
      this.stats.snapshotsPerSec = this.snapCount / dt;
      this.stats.loss = this.snapGap.expected > 0 ? Math.max(0, 1 - this.snapGap.got / this.snapGap.expected) * 100 : 0;
      this.snapCount = 0; this.snapGap = { expected: 0, got: 0 };
      this.bytesMark = { t: now, in: this.transport.bytesIn, out: this.transport.bytesOut };
      this.ping();
    }
    this.stats.ping = this.rtt;
    this.stats.jitter = this.jitterTicks * MS_PER_TICK;
    this.stats.cushion = this.cushion;
    this.stats.corrections = this.corrWindow.length;
    this.stats.interp = this.interpDelay * MS_PER_TICK;
    this.stats.tickRate = this.srvRate * 1000 * this.rate;
  }

  close() { this.transport.close(); }
}

export { HISTORY_TICKS };
